import {
  AdminStatusDto,
  CandidateItemDto,
  type CandidateSearchRequestDto,
  CandidateStatusDto,
  GenderDto,
  QuizScoreDto,
} from '@/api/generated';

import { type CandidatesTranslations, candidatesTranslations } from './candidates.translations.pl.ts';
import { type DashboardTranslations, dashboardTranslations } from './dashboard.translations.pl.ts';
import { type FairsTranslations, fairsTranslations } from './fairs.translations.pl.ts';
import { type ReportsTranslations, reportsTranslations } from './reports.translations.pl.ts';

export type AdminViewTranslations = {
  candidates: CandidatesTranslations;
  dashboard: DashboardTranslations;
  details: {
    adminStatus: string;
    adminStatuses: Record<AdminStatusDto, string>;
    phone: string;
  };
  preview: {
    sections: {
      additional: string;
      basic: string;
      dates: string;
      statusAndResults: string;
    };
  };
  filters: {
    actionPossibleFrom: string;
    actionPossibleTo: string;
    adminStatus: string;
    adminStatuses: Record<Exclude<AdminStatusDto, typeof AdminStatusDto.None>, string>;
    title: string;
  };
  pointsLegend: {
    decision: string;
    doNotSend: string;
    max: string;
    min: string;
    score: string;
    send: string;
    title: string;
  };
  actions: {
    cancel: string;
    confirm: string;
    confirmMessages: {
      default: string;
      negativeFeedback: string;
      onHold: string;
      positiveFeedback: string;
    };
    confirmTitle: string;
    genderLabel: string;
    genderTitle: string;
    labels: {
      gender: string;
      negativeFeedback: string;
      onHold: string;
      positiveFeedback: string;
    };
    noActions: string;
    save: string;
    toggle: string;
  };
  search: Record<keyof Omit<CandidateSearchRequestDto, 'sort' | 'areaOfInterests'>, string>;
  buttons: {
    submit: string;
    clear: string;
    logout: string;
  };
  tabs: {
    candidates: string;
    dashboard: string;
    fairCandidates: string;
    reports: string;
  };
  fairs: FairsTranslations;
  reports: ReportsTranslations;
  tooltips: {
    downloadCv: string;
    downloadFullAudit: string;
    downloadProcessCv: string;
    setPositiveFeedback: string;
    viewCandidate: string;
  };
  table: Record<
    keyof Omit<
      CandidateItemDto,
      'candidateId' | 'firstName' | 'lastName' | 'phone' | 'noOfLogins' | 'adminStatus'
    >,
    string
  > & {
    name: string;
    statuses: Record<CandidateStatusDto, string>;
    quizScores: Record<QuizScoreDto, string>;
    genders: Record<GenderDto, string>;
    yes: string;
    no: string;
  };
  confirmPositiveFeedbackModal: {
    body: string;
    closeLabel: string;
    confirmLabel: string;
    title: string;
  };
};

export const adminViewTranslations: AdminViewTranslations = {
  candidates: candidatesTranslations,
  dashboard: dashboardTranslations,
  details: {
    adminStatus: 'Status admina',
    adminStatuses: {
      [AdminStatusDto.None]: 'Brak',
      [AdminStatusDto.OnHoldAdmin]: 'Wstrzymany (admin)',
      [AdminStatusDto.OnHoldConditional]: 'Wstrzymany (warunkowy)',
    },
    phone: 'Telefon',
  },
  preview: {
    sections: {
      additional: 'Informacje dodatkowe',
      basic: 'Dane podstawowe',
      dates: 'Daty',
      statusAndResults: 'Status i wyniki',
    },
  },
  filters: {
    actionPossibleFrom: 'Ocena możliwa od',
    actionPossibleTo: 'Ocena możliwa do',
    adminStatus: 'Status administratora',
    adminStatuses: {
      [AdminStatusDto.OnHoldAdmin]: 'Wstrzymane - Admin',
      [AdminStatusDto.OnHoldConditional]: 'Wstrzymane - Warunkowo',
    },
    title: 'Filtry',
  },
  pointsLegend: {
    decision: 'Decyzja',
    doNotSend: 'Nie wysyłaj',
    max: 'Max',
    min: 'Min',
    score: 'Wynik',
    send: 'Wyślij',
    title: 'Legenda punktów',
  },
  actions: {
    cancel: 'Anuluj',
    confirm: 'Potwierdź',
    confirmMessages: {
      default: 'Czy na pewno chcesz wykonać tę akcję?',
      negativeFeedback: 'Czy na pewno chcesz ustawić negatywny feedback dla tego kandydata?',
      onHold: 'Czy na pewno chcesz wstrzymać proces dla tego kandydata?',
      positiveFeedback: 'Czy na pewno chcesz ustawić pozytywny feedback dla tego kandydata?',
    },
    confirmTitle: 'Potwierdź akcję',
    genderLabel: 'Płeć kandydata',
    genderTitle: 'Wybierz płeć',
    labels: {
      gender: 'Ustaw płeć',
      negativeFeedback: 'Ustaw negatywny feedback',
      onHold: 'Wstrzymaj proces',
      positiveFeedback: 'Ustaw pozytywny feedback',
    },
    noActions: 'Brak dostępnych akcji',
    save: 'Zapisz',
    toggle: 'Akcje',
  },
  confirmPositiveFeedbackModal: {
    title: 'Potwierdź pozytywną opinię',
    body: 'Czy na pewno chcesz potwierdzić pozytywną opinię?',
    closeLabel: 'Anuluj',
    confirmLabel: 'Potwierdź',
  },
  search: {
    email: 'Email',
    phone: 'Numer telefonu',
    statuses: 'Status',
    lastName: 'Nazwisko',
    quizScore: 'Wynik quizu',
    locationIds: 'Lokalizacje',
    adminActionPossibleBeforeFrom: 'Akcja możliwa od',
    adminActionPossibleBeforeTo: 'Akcja możliwa do',
    isFromFair: 'Z targów pracy',
    adminStatuses: 'Statusy administracyjne',
  },
  buttons: {
    submit: 'Szukaj',
    clear: 'Czyść',
    logout: 'Wyloguj',
  },
  tabs: {
    candidates: 'Kandydaci',
    dashboard: 'Dashboard',
    fairCandidates: 'Targi pracy',
    reports: 'Raporty',
  },
  fairs: fairsTranslations,
  reports: reportsTranslations,
  tooltips: {
    downloadCv: 'Pobierz CV',
    downloadFullAudit: 'Pobierz pełny audyt kandydata',
    downloadProcessCv: 'Pobierz plik procesu',
    setPositiveFeedback: 'Zmień ocenę na pozytywną',
    viewCandidate: 'Podgląd kandydata',
  },
  table: {
    decisionOverridden: 'Decyzja nadpisana',
    name: 'Kandydat',
    email: 'Email',
    status: 'Status',
    quizScore: 'Wynik quizu',
    sourceApp: 'Źródło',
    noOfQuizRevisits: 'Licz. wejść',
    areasOfInterests: 'Obszary biznesowe',
    location: 'Lokalizacja',
    university: 'Uczelnia',
    gender: 'Płeć',
    isFromFair: 'Z targów',
    statuses: {
      [CandidateStatusDto.SentToERecruiter]: 'Wysłano do eRecruitera',
      [CandidateStatusDto.PositiveFeedback]: 'Wynik gry pozytywny',
      [CandidateStatusDto.NegativeFeedback]: 'Wynik gry negatywny',
      [CandidateStatusDto.PositiveFeedbackSent]: 'Wysłano feedback pozytywny',
      [CandidateStatusDto.NegativeFeedbackSent]: 'Wysłano feedback negatywny',
      [CandidateStatusDto.InQuiz]: 'W trakcie gry',
      [CandidateStatusDto.QuizFinished]: 'Gra zakończona',
      [CandidateStatusDto.PersonalDataProvided]: 'Podane dane osobowe',
      [CandidateStatusDto.EmailProvided]: 'Email podany',
      [CandidateStatusDto.CandidateProcessFinished]: 'Proces zakończony',
      [CandidateStatusDto.EmailVerified]: 'Email zweryfikowany',
      [CandidateStatusDto.QuizExpired]: 'Przekroczony czas w grze',
      [CandidateStatusDto.QuizRevisitsExceeded]: 'Przekroczona liczba wejść w gre',
      [CandidateStatusDto.QuizPointsResultReady]: 'Wynik punktowy gotowy',
    },
    quizScores: {
      [QuizScoreDto.Low]: 'Niski',
      [QuizScoreDto.Average]: 'Średni',
      [QuizScoreDto.Good]: 'Dobry',
      [QuizScoreDto.VeryGood]: 'Bardzo dobry',
      [QuizScoreDto.Outstanding]: 'Doskonały',
    },
    genders: {
      [GenderDto.Male]: 'Mężczyzna',
      [GenderDto.Female]: 'Kobieta',
      [GenderDto.Unknown]: 'Nieznana',
    },
    quizPoints: 'Punkty',
    statusUpdatedAt: 'Data zmiany statusu',
    quizFinishedAt: 'Quiz zakończony',
    adminActionPossibleBefore: 'Ocena możliwa do',
    possibleAdminActionTypes: 'Dostępne akcje',
    yes: 'Tak',
    no: 'Nie',
  },
};
